<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  accessLevel: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close']);
const store = useStore();
const { t } = useI18n();

const name = ref('');
const description = ref('');
const selectedCapabilities = ref([]);
const isUpdating = ref(false);

const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const hasBranchManagement = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.BRANCH_MANAGEMENT
  );
});

const hasSalesFunnel = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.SALES_FUNNEL
  );
});

// Static list of capabilities organized by category
const capabilityGroups = computed(() => [
  {
    title: t('ACCESS_LEVELS.FORM.CAPABILITIES.GROUPS.CONVERSATIONS'),
    items: [
      {
        key: 'conversation_view_all',
        label: t('ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.conversation_view_all'),
      },
      {
        key: 'conversation_view_unassigned',
        label: t(
          'ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.conversation_view_unassigned'
        ),
      },
      {
        key: 'conversation_delete',
        label: t('ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.conversation_delete'),
      },
      ...(hasBranchManagement.value
        ? [
            {
              key: 'branch_transfer',
              label: t('ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.branch_transfer'),
            },
            {
              key: 'conversation_view_branchless',
              label: t(
                'ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.conversation_view_branchless'
              ),
            },
          ]
        : []),
    ],
  },
  {
    title: t('ACCESS_LEVELS.FORM.CAPABILITIES.GROUPS.CONTACTS'),
    items: [
      {
        key: 'contact_view',
        label: t('ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.contact_view'),
      },
      {
        key: 'contact_create',
        label: t('ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.contact_create'),
      },
      {
        key: 'contact_edit',
        label: t('ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.contact_edit'),
      },
      {
        key: 'contact_import_export',
        label: t('ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.contact_import_export'),
      },
    ],
  },
  {
    title: t('ACCESS_LEVELS.FORM.CAPABILITIES.GROUPS.MANAGEMENT'),
    items: [
      {
        key: 'report_view',
        label: t('ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.report_view'),
      },
      {
        key: 'team_manage',
        label: t('ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.team_manage'),
      },
      {
        key: 'label_manage',
        label: t('ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.label_manage'),
      },
      {
        key: 'canned_manage',
        label: t('ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.canned_manage'),
      },
      {
        key: 'automation_manage',
        label: t('ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.automation_manage'),
      },
      {
        key: 'inbox_manage',
        label: t('ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.inbox_manage'),
      },
      {
        key: 'campaign_manage',
        label: t('ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.campaign_manage'),
      },
      {
        key: 'agent_manage',
        label: t('ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.agent_manage'),
      },
      ...(hasBranchManagement.value
        ? [
            {
              key: 'branch_manage',
              label: t('ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.branch_manage'),
            },
          ]
        : []),
    ],
  },
  ...(hasSalesFunnel.value
    ? [
        {
          title: t('ACCESS_LEVELS.FORM.CAPABILITIES.GROUPS.SALES_FUNNEL'),
          items: [
            {
              key: 'sales_funnel_view',
              label: t(
                'ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.sales_funnel_view'
              ),
            },
            {
              key: 'sales_funnel_view_all',
              label: t(
                'ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.sales_funnel_view_all'
              ),
            },
            {
              key: 'sales_funnel_view_unassigned',
              label: t(
                'ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.sales_funnel_view_unassigned'
              ),
            },
            {
              key: 'sales_funnel_use',
              label: t(
                'ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.sales_funnel_use'
              ),
            },
            {
              key: 'sales_funnel_delete',
              label: t(
                'ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.sales_funnel_delete'
              ),
            },
            {
              key: 'sales_funnel_export',
              label: t(
                'ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.sales_funnel_export'
              ),
            },
            {
              key: 'sales_funnel_manage_stages',
              label: t(
                'ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.sales_funnel_manage_stages'
              ),
            },
            {
              key: 'sales_funnel_manage_products',
              label: t(
                'ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.sales_funnel_manage_products'
              ),
            },
            {
              key: 'sales_funnel_manage_lost_reasons',
              label: t(
                'ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.sales_funnel_manage_lost_reasons'
              ),
            },
            {
              key: 'sales_funnel_manage_labels',
              label: t(
                'ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.sales_funnel_manage_labels'
              ),
            },
            {
              key: 'sales_funnel_reports',
              label: t(
                'ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.sales_funnel_reports'
              ),
            },
            {
              key: 'sales_funnel_activity_log',
              label: t(
                'ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.sales_funnel_activity_log'
              ),
            },
          ],
        },
      ]
    : []),
  {
    title: t('ACCESS_LEVELS.FORM.CAPABILITIES.GROUPS.INTERNAL_CHAT'),
    items: [
      {
        key: 'internal_chat_direct_message',
        label: t(
          'ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.internal_chat_direct_message'
        ),
      },
      {
        key: 'internal_chat_create_group',
        label: t(
          'ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.internal_chat_create_group'
        ),
      },
      {
        key: 'internal_chat_delete_messages',
        label: t(
          'ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.internal_chat_delete_messages'
        ),
      },
      {
        key: 'internal_chat_delete_own_messages',
        label: t(
          'ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.internal_chat_delete_own_messages'
        ),
      },
      {
        key: 'internal_chat_supervise',
        label: t(
          'ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.internal_chat_supervise'
        ),
      },
      {
        key: 'internal_chat_delete_conversation',
        label: t(
          'ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.internal_chat_delete_conversation'
        ),
      },
    ],
  },
  {
    title: t('ACCESS_LEVELS.FORM.CAPABILITIES.GROUPS.AUDIT'),
    items: [
      {
        key: 'activity_logs_view',
        label: t('ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.activity_logs_view'),
      },
      {
        key: 'system_logs_view',
        label: t('ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.system_logs_view'),
      },
      {
        key: 'conversation_view_deleted',
        label: t(
          'ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.conversation_view_deleted'
        ),
      },
    ],
  },
  {
    title: t('ACCESS_LEVELS.FORM.CAPABILITIES.GROUPS.MOBILE_APP'),
    items: [
      {
        key: 'mobile_app_search_tool',
        label: t(
          'ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.mobile_app_search_tool'
        ),
      },
      ...(hasSalesFunnel.value
        ? [
            {
              key: 'mobile_app_register_prospects',
              label: t(
                'ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.mobile_app_register_prospects'
              ),
            },
          ]
        : []),
      {
        key: 'mobile_app_internal_chat_view',
        label: t(
          'ACCESS_LEVELS.FORM.CAPABILITIES.ITEMS.mobile_app_internal_chat_view'
        ),
      },
    ],
  },
]);

onMounted(() => {
  name.value = props.accessLevel.name || '';
  description.value = props.accessLevel.description || '';
  selectedCapabilities.value = [...(props.accessLevel.capabilities || [])];
});

const toggleCapability = key => {
  const index = selectedCapabilities.value.indexOf(key);
  if (index === -1) {
    selectedCapabilities.value.push(key);
  } else {
    selectedCapabilities.value.splice(index, 1);
  }
};

const isSelected = key => selectedCapabilities.value.includes(key);

const selectAllInGroup = group => {
  const allSelected = group.items.every(item => isSelected(item.key));
  if (allSelected) {
    selectedCapabilities.value = selectedCapabilities.value.filter(
      key => !group.items.some(item => item.key === key)
    );
  } else {
    group.items.forEach(item => {
      if (!selectedCapabilities.value.includes(item.key)) {
        selectedCapabilities.value.push(item.key);
      }
    });
  }
};

const countSelectedInGroup = group => {
  return group.items.filter(item => isSelected(item.key)).length;
};

const selectAllCapabilities = () => {
  const allKeys = [];
  capabilityGroups.value.forEach(group => {
    group.items.forEach(item => {
      if (!allKeys.includes(item.key)) allKeys.push(item.key);
    });
  });
  selectedCapabilities.value = allKeys;
};

const deselectAllCapabilities = () => {
  selectedCapabilities.value = [];
};

const updateAccessLevel = async () => {
  if (!name.value.trim()) {
    useAlert(t('ACCESS_LEVELS.EDIT.API.NAME_REQUIRED'));
    return;
  }

  isUpdating.value = true;
  try {
    await store.dispatch('accessLevels/update', {
      id: props.accessLevel.id,
      name: name.value,
      description: description.value,
      capabilities: selectedCapabilities.value,
    });
    useAlert(t('ACCESS_LEVELS.EDIT.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    useAlert(error?.message || t('ACCESS_LEVELS.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isUpdating.value = false;
  }
};

const onClose = () => {
  emit('close');
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-y-auto max-h-[85vh] w-full">
    <woot-modal-header
      :header-title="$t('ACCESS_LEVELS.EDIT.TITLE')"
      :header-content="$t('ACCESS_LEVELS.EDIT.DESC')"
    />
    <form
      class="flex flex-col w-full gap-5 p-6"
      @submit.prevent="updateAccessLevel"
    >
      <!-- Top Inputs: Nombre y Descripción en 2 columnas -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
        <div>
          <label class="mb-1.5 block text-xs font-semibold text-n-slate-12">
            {{ $t('ACCESS_LEVELS.FORM.NAME.LABEL') }}
            <span class="text-n-ruby-9">*</span>
          </label>
          <input
            v-model.trim="name"
            type="text"
            class="w-full !h-9 !py-0 !m-0 border border-n-weak rounded-xl px-3 bg-white dark:bg-n-solid-2 text-n-slate-12 placeholder-n-slate-10 text-xs focus:outline-none focus:ring-1 focus:ring-n-brand transition-all"
            :placeholder="$t('ACCESS_LEVELS.FORM.NAME.PLACEHOLDER')"
          />
        </div>

        <div>
          <label class="mb-1.5 block text-xs font-semibold text-n-slate-12">
            {{ $t('ACCESS_LEVELS.FORM.DESCRIPTION.LABEL') }}
          </label>
          <input
            v-model.trim="description"
            type="text"
            class="w-full !h-9 !py-0 !m-0 border border-n-weak rounded-xl px-3 bg-white dark:bg-n-solid-2 text-n-slate-12 placeholder-n-slate-10 text-xs focus:outline-none focus:ring-1 focus:ring-n-brand transition-all"
            :placeholder="$t('ACCESS_LEVELS.FORM.DESCRIPTION.PLACEHOLDER')"
          />
        </div>
      </div>

      <!-- Sección de Permisos / Capacidades estilo Image 2 -->
      <div class="w-full flex flex-col gap-3">
        <div class="flex items-center justify-between border-b border-n-weak pb-2">
          <div class="flex items-center gap-2">
            <h3 class="text-sm font-bold text-n-slate-12">
              {{ $t('ACCESS_LEVELS.FORM.CAPABILITIES.LABEL') }}
            </h3>
            <span class="text-xs text-n-slate-10 font-medium">
              ({{ selectedCapabilities.length }} {{ $t('ACCESS_LEVELS.FORM.SELECTED_COUNT') }})
            </span>
          </div>
          <div class="flex items-center gap-2 text-xs">
            <button
              type="button"
              class="text-n-brand hover:underline font-medium cursor-pointer"
              @click="selectAllCapabilities"
            >
              {{ $t('ACCESS_LEVELS.FORM.SELECT_ALL') }}
            </button>
            <span class="text-n-slate-7">|</span>
            <button
              type="button"
              class="text-n-slate-10 hover:underline font-medium cursor-pointer"
              @click="deselectAllCapabilities"
            >
              {{ $t('ACCESS_LEVELS.FORM.DESELECT_ALL') }}
            </button>
          </div>
        </div>

        <!-- Tarjetas de Módulos (Grid similar a Imagen 2) -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
            v-for="group in capabilityGroups"
            :key="group.title"
            class="rounded-xl border border-n-weak bg-white dark:bg-n-solid-2 p-4 flex flex-col gap-3 shadow-sm hover:border-n-slate-6 dark:hover:border-n-slate-7 transition-colors"
          >
            <!-- Título del grupo dentro de la tarjeta -->
            <div class="flex items-center justify-between border-b border-n-weak/50 pb-2">
              <h4 class="text-xs font-bold text-n-slate-12 tracking-wide uppercase">
                {{ group.title }}
              </h4>
              <button
                type="button"
                class="text-[11px] font-medium text-n-slate-10 hover:text-n-brand transition-colors cursor-pointer"
                :title="$t('ACCESS_LEVELS.FORM.SELECT_ALL')"
                @click="selectAllInGroup(group)"
              >
                {{ countSelectedInGroup(group) }}/{{ group.items.length }}
              </button>
            </div>

            <!-- Lista de permisos en columnas (como en Imagen 2) -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2.5">
              <label
                v-for="cap in group.items"
                :key="cap.key"
                class="flex items-start gap-2 cursor-pointer group select-none py-0.5"
              >
                <input
                  type="checkbox"
                  :checked="isSelected(cap.key)"
                  class="mt-0.5 rounded border-n-slate-7 text-n-brand focus:ring-n-brand cursor-pointer size-4 shrink-0 transition-colors"
                  @change="toggleCapability(cap.key)"
                />
                <span
                  class="text-xs text-n-slate-12 group-hover:text-n-brand transition-colors leading-snug"
                  :class="{ 'font-semibold text-n-brand': isSelected(cap.key) }"
                >
                  {{ cap.label }}
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer Actions -->
      <div
        class="flex flex-row justify-end items-center gap-2 pt-4 border-t border-n-weak"
      >
        <NextButton
          sm
          ghost
          slate
          type="button"
          :label="$t('ACCESS_LEVELS.FORM.CANCEL')"
          @click="onClose"
        />
        <NextButton
          sm
          type="submit"
          :is-loading="isUpdating"
          :label="isUpdating ? $t('ACCESS_LEVELS.FORM.UPDATING') : $t('ACCESS_LEVELS.FORM.UPDATE')"
        />
      </div>
    </form>
  </div>
</template>
